import { useState } from "react";
import { supabase } from "../supabaseClient";

export function usePracticeQuestions(session) {
  const [practiceQuestions, setPracticeQuestions] =
    useState([]);

  const [
    loadingPracticeQuestions,
    setLoadingPracticeQuestions,
  ] = useState(false);

  const [
    currentPracticeSessionId,
    setCurrentPracticeSessionId,
  ] = useState(null);

  const [
    startingPracticeSession,
    setStartingPracticeSession,
  ] = useState(false);

  const [
    submittingPracticeAnswer,
    setSubmittingPracticeAnswer,
  ] = useState(false);

  // =========================================================
  // FETCH QUESTION MEDIA
  //
  // Loads active media records for the selected questions
  // and creates temporary signed URLs for the private
  // question-media Storage bucket.
  // =========================================================

  const fetchQuestionMedia = async (
    questionIds = []
  ) => {
    if (
      !Array.isArray(questionIds) ||
      questionIds.length === 0
    ) {
      return new Map();
    }

    const {
      data: mediaRows,
      error: mediaError,
    } = await supabase
      .from("question_media")
      .select(
        `
        id,
        question_id,
        media_type,
        storage_bucket,
        storage_path,
        alt_text,
        caption,
        display_order,
        status
        `
      )
      .in(
        "question_id",
        questionIds
      )
      .eq(
        "status",
        "active"
      )
      .order(
        "display_order",
        {
          ascending: true,
        }
      );

    if (mediaError) {
      console.error(
        "FETCH QUESTION MEDIA ERROR:",
        mediaError
      );

      return new Map();
    }

    const signedMediaRows =
      await Promise.all(
        (mediaRows || []).map(
          async (media) => {
            const {
              data: signedData,
              error: signedError,
            } = await supabase.storage
              .from(
                media.storage_bucket
              )
              .createSignedUrl(
                media.storage_path,
                3600
              );

            if (signedError) {
              console.error(
                "CREATE QUESTION MEDIA SIGNED URL ERROR:",
                signedError
              );

              return {
                ...media,
                signed_url: null,
              };
            }

            return {
              ...media,

              signed_url:
                signedData?.signedUrl ||
                null,
            };
          }
        )
      );

    const mediaMap =
      new Map();

    signedMediaRows.forEach(
      (media) => {
        if (
          !media.question_id ||
          !media.signed_url
        ) {
          return;
        }

        const existing =
          mediaMap.get(
            media.question_id
          ) || [];

        existing.push(
          media
        );

        mediaMap.set(
          media.question_id,
          existing
        );
      }
    );

    return mediaMap;
  };


  // =========================================================
  // FETCH PRACTICE QUESTIONS
  //
  // Adaptive selection happens ONCE at the beginning
  // of each round.
  //
  // The returned question order is preserved.
  // =========================================================

  const fetchPracticeQuestions = async ({
    limit = 10,
    domainId = null,
    topicId = null,
  } = {}) => {
    setLoadingPracticeQuestions(true);

    try {
      // =======================================================
      // 1. GET ADAPTIVE QUESTION IDS
      // =======================================================

      const {
        data: adaptiveRows,
        error: adaptiveError,
      } = await supabase.rpc(
        "get_adaptive_practice_question_ids",
        {
          p_limit: limit,
          p_domain_id: domainId,
          p_topic_id: topicId,
        }
      );

      if (adaptiveError) {
        console.error(
          "FETCH ADAPTIVE PRACTICE QUESTION IDS ERROR:",
          adaptiveError
        );

        setPracticeQuestions([]);
        setLoadingPracticeQuestions(false);

        return [];
      }

      // =======================================================
      // 3. QUESTION IDS
      // =======================================================

      const questionIds =
        (adaptiveRows || [])
          .map(
            (row) =>
              row.question_id
          )
          .filter(Boolean);

      if (!questionIds.length) {
        setPracticeQuestions([]);
        setLoadingPracticeQuestions(false);

        return [];
      }


      // =======================================================
      // 4. FETCH QUESTION CONTENT
      // =======================================================

      const {
        data: questionRows,
        error: questionError,
      } = await supabase
        .from("questions")
        .select(
          `
          id,
          question_text,
          difficulty_level,
          domain_id,
          topic_id
          `
        )
        .in(
          "id",
          questionIds
        );

      if (questionError) {
        console.error(
          "FETCH PRACTICE QUESTIONS ERROR:",
          questionError
        );

        setPracticeQuestions([]);
        setLoadingPracticeQuestions(false);

        return [];
      }


      // =======================================================
      // 5. FETCH QUESTION OPTIONS
      // =======================================================

      const {
        data: optionRows,
        error: optionError,
      } = await supabase
        .from("question_options")
        .select(
          `
          id,
          question_id,
          option_key,
          option_text,
          display_order
          `
        )
        .in(
          "question_id",
          questionIds
        )
        .order(
          "display_order",
          {
            ascending: true,
          }
        );

      if (optionError) {
        console.error(
          "FETCH PRACTICE OPTIONS ERROR:",
          optionError
        );

        setPracticeQuestions([]);
        setLoadingPracticeQuestions(false);

        return [];
      }

      // =======================================================
      // 5B. FETCH QUESTION MEDIA
      // =======================================================

      const mediaMap =
        await fetchQuestionMedia(
          questionIds
        );


      // =======================================================
      // 6. PRESERVE ADAPTIVE ORDER
      //
      // Supabase .in() does not guarantee that rows
      // return in the same order as questionIds.
      //
      // Reconstruct the list using the adaptive RPC order.
      // =======================================================

      const questionMap =
        new Map(
          (questionRows || []).map(
            (question) => [
              question.id,
              question,
            ]
          )
        );

      const orderedQuestionRows =
        questionIds
          .map(
            (id) =>
              questionMap.get(id)
          )
          .filter(Boolean);


      // =======================================================
      // 7. NORMALIZE FOR PRACTICE UI
      // =======================================================

      const normalizedQuestions =
        orderedQuestionRows.map(
          (question) => {
            const options =
              (optionRows || [])
                .filter(
                  (option) =>
                    option.question_id ===
                    question.id
                )
                .sort(
                  (a, b) =>
                    a.display_order -
                    b.display_order
                );

            return {
              id:
                question.id,

              question:
                question.question_text,

              difficulty:
                question.difficulty_level,

              domain_id:
                question.domain_id,

              topic_id:
                question.topic_id,

              options:
                options.map(
                  (option) =>
                    option.option_text
                ),

              optionRecords:
                options,

              media:
                mediaMap.get(
                  question.id
                ) || [],
            };
          }
        );


      // =======================================================
      // 8. SAVE ROUND QUESTIONS TO REACT STATE
      // =======================================================

      setPracticeQuestions(
        normalizedQuestions
      );

      setLoadingPracticeQuestions(
        false
      );

      return normalizedQuestions;
    } catch (error) {
      console.error(
        "FETCH PRACTICE QUESTIONS UNEXPECTED ERROR:",
        error
      );

      setPracticeQuestions([]);

      setLoadingPracticeQuestions(
        false
      );

      return [];
    }
  };

  // =========================================================
  // FETCH WRONG QUESTIONS
  //
  // Gets the current student's wrong questions from
  // get_wrong_question_ids().
  //
  // The database decides review priority.
  // The returned order is preserved.
  //
  // filter:
  //   "due"
  //   "active"
  //   "mastered"
  // =========================================================

  const fetchWrongQuestions = async ({
    limit = 10,
    filter = "due",
    domainId = null,
    topicId = null,
  } = {}) => {
    setLoadingPracticeQuestions(true);

    try {

      // =======================================================
      // 1. GET WRONG QUESTION IDS
      // =======================================================

      const {
        data: wrongRows,
        error: wrongError,
      } = await supabase.rpc(
        "get_wrong_question_ids",
        {
          p_limit: limit,
          p_filter: filter,
          p_domain_id: domainId,
          p_topic_id: topicId,
        }
      );


      if (wrongError) {
        console.error(
          "FETCH WRONG QUESTION IDS ERROR:",
          wrongError
        );

        setPracticeQuestions([]);

        return [];
      }

      // =======================================================
      // 3. QUESTION IDS
      // =======================================================

      const questionIds =
        (wrongRows || [])
          .map(
            (row) =>
              row.question_id
          )
          .filter(Boolean);


      if (!questionIds.length) {
        setPracticeQuestions([]);

        return [];
      }


      // =======================================================
      // 4. FETCH QUESTION CONTENT
      // =======================================================

      const {
        data: questionRows,
        error: questionError,
      } = await supabase
        .from("questions")
        .select(
          `
          id,
          question_text,
          difficulty_level,
          domain_id,
          topic_id
          `
        )
        .in(
          "id",
          questionIds
        );


      if (questionError) {
        console.error(
          "FETCH WRONG QUESTIONS ERROR:",
          questionError
        );

        setPracticeQuestions([]);

        return [];
      }


      // =======================================================
      // 5. FETCH OPTIONS
      // =======================================================

      const {
        data: optionRows,
        error: optionError,
      } = await supabase
        .from("question_options")
        .select(
          `
          id,
          question_id,
          option_key,
          option_text,
          display_order
          `
        )
        .in(
          "question_id",
          questionIds
        )
        .order(
          "display_order",
          {
            ascending: true,
          }
        );


      if (optionError) {
        console.error(
          "FETCH WRONG QUESTION OPTIONS ERROR:",
          optionError
        );

        setPracticeQuestions([]);

        return [];
      }
      
      // =======================================================
      // 5B. FETCH QUESTION MEDIA
      // =======================================================

      const mediaMap =
        await fetchQuestionMedia(
          questionIds
        );

      // =======================================================
      // 6. PRESERVE WRONG-QUESTION PRIORITY ORDER
      // =======================================================

      const questionMap =
        new Map(
          (questionRows || []).map(
            (question) => [
              question.id,
              question,
            ]
          )
        );


      const wrongProgressMap =
        new Map(
          (wrongRows || []).map(
            (row) => [
              row.question_id,
              row,
            ]
          )
        );


      const orderedQuestionRows =
        questionIds
          .map(
            (id) =>
              questionMap.get(id)
          )
          .filter(Boolean);


      // =======================================================
      // 7. NORMALIZE FOR EXISTING PRACTICE UI
      //
      // Important:
      // We keep the SAME structure used by PracticeQuestionPage.
      // Therefore Wrong Review can reuse the existing page.
      // =======================================================

      const normalizedQuestions =
        orderedQuestionRows.map(
          (question) => {

            const options =
              (optionRows || [])
                .filter(
                  (option) =>
                    option.question_id ===
                    question.id
                )
                .sort(
                  (a, b) =>
                    a.display_order -
                    b.display_order
                );


            const wrongProgress =
              wrongProgressMap.get(
                question.id
              );


            return {
              id:
                question.id,

              question:
                question.question_text,

              difficulty:
                question.difficulty_level,

              domain_id:
                question.domain_id,

              topic_id:
                question.topic_id,

              options:
                options.map(
                  (option) =>
                    option.option_text
                ),

              optionRecords:
                options,

              media:
                mediaMap.get(
                  question.id
                ) || [],

              // -------------------------------------------------
              // Wrong Question metadata
              //
              // PracticeQuestionPage does not need these yet,
              // but Wrong Question UI can use them later.
              // -------------------------------------------------

              wrongQuestionMeta: {
                masteryLevel:
                  wrongProgress?.mastery_level ??
                  0,

                incorrectCount:
                  wrongProgress?.incorrect_count ??
                  0,

                attemptCount:
                  wrongProgress?.attempt_count ??
                  0,

                consecutiveCorrect:
                  wrongProgress?.consecutive_correct ??
                  0,

                firstWrongAt:
                  wrongProgress?.first_wrong_at ??
                  null,

                lastWrongAt:
                  wrongProgress?.last_wrong_at ??
                  null,

                nextReviewAt:
                  wrongProgress?.next_review_at ??
                  null,

                reviewPriority:
                  wrongProgress?.review_priority !==
                  undefined
                    ? Number(
                        wrongProgress.review_priority
                      )
                    : null,
              },
            };
          }
        );


      // =======================================================
      // 8. SAVE TO SAME PRACTICE STATE
      // =======================================================

      setPracticeQuestions(
        normalizedQuestions
      );


      return normalizedQuestions;

    } catch (error) {

      console.error(
        "FETCH WRONG QUESTIONS UNEXPECTED ERROR:",
        error
      );

      setPracticeQuestions([]);

      return [];

    } finally {

      setLoadingPracticeQuestions(
        false
      );

    }
  };

  // =========================================================
  // START PRACTICE SESSION
  //
  // Important:
  //
  // `questions` must be the SAME frozen question list
  // returned by fetchPracticeQuestions().
  //
  // We do NOT run the adaptive engine again here.
  // =========================================================

  const startPracticeSession = async ({
    practiceMode,
    domainId = null,
    topicId = null,
    difficultyMode = "adaptive",
    questions = [],
  }) => {
    if (!session?.user?.id) {
      alert(
        "Please log in first."
      );

      return null;
    }

    if (
      !Array.isArray(questions) ||
      questions.length === 0
    ) {
      console.error(
        "START PRACTICE SESSION ERROR: No frozen questions were provided."
      );

      alert(
        "Unable to start practice because no questions were selected."
      );

      return null;
    }

    setStartingPracticeSession(
      true
    );

    try {
      // =======================================================
      // 1. CREATE PRACTICE SESSION
      // =======================================================

      const {
        data: sessionData,
        error: sessionError,
      } = await supabase
        .from("practice_sessions")
        .insert({
          user_id:
            session.user.id,

          practice_mode:
            practiceMode,

          domain_id:
            domainId,

          topic_id:
            topicId,

          difficulty_mode:
            difficultyMode,

          session_status:
            "in_progress",
        })
        .select("id")
        .single();

      if (sessionError) {
        console.error(
          "START PRACTICE SESSION ERROR:",
          sessionError
        );

        alert(
          sessionError.message
        );

        setStartingPracticeSession(
          false
        );

        return null;
      }


      const practiceSessionId =
        sessionData.id;


      // =======================================================
      // 2. PREPARE FROZEN QUESTION ROWS
      // =======================================================

      const frozenQuestions =
        questions.map(
          (question, index) => ({
            practice_session_id:
              practiceSessionId,

            question_id:
              typeof question ===
              "string"
                ? question
                : question.id,

            display_order:
              index + 1,
          })
        );


      // =======================================================
      // 3. INSERT FROZEN QUESTION ORDER
      // =======================================================

      const {
        error: frozenQuestionsError,
      } = await supabase
        .from(
          "practice_session_questions"
        )
        .insert(
          frozenQuestions
        );

      if (frozenQuestionsError) {
        console.error(
          "FREEZE PRACTICE QUESTIONS ERROR:",
          frozenQuestionsError
        );


        // -----------------------------------------------------
        // Cleanup incomplete session.
        //
        // We do not want an in_progress session without
        // its frozen question list.
        // -----------------------------------------------------

        const {
          error: cleanupError,
        } = await supabase
          .from(
            "practice_sessions"
          )
          .delete()
          .eq(
            "id",
            practiceSessionId
          )
          .eq(
            "user_id",
            session.user.id
          );

        if (cleanupError) {
          console.error(
            "PRACTICE SESSION CLEANUP ERROR:",
            cleanupError
          );
        }

        alert(
          "Unable to freeze this practice round."
        );

        setStartingPracticeSession(
          false
        );

        return null;
      }

      // =======================================================
      // 5. SAVE CURRENT SESSION
      //
      // React state:
      // used during the current browser session.
      //
      // localStorage:
      // used to restore this EXACT session after refresh.
      // =======================================================

      setCurrentPracticeSessionId(
        practiceSessionId
      );

      localStorage.setItem(
        "active_practice_session_id",
        practiceSessionId
      );

      setStartingPracticeSession(
        false
      );

      return practiceSessionId;
    } catch (error) {
      console.error(
        "START PRACTICE SESSION UNEXPECTED ERROR:",
        error
      );

      setStartingPracticeSession(
        false
      );

      return null;
    }
  };

  // =========================================================
  // RESUME PRACTICE SESSION
  //
  // Restores:
  // - latest in-progress session
  // - frozen question order
  // - question content
  // - options
  // - previous answers
  // =========================================================

  const resumePracticeSession = async ({
    practiceSessionId = null,
    practiceMode = null,
    domainId = null,
    topicId = null,
  } = {}) => {
    if (!session?.user?.id) {
      return null;
    }

    setLoadingPracticeQuestions(true);

    try {
      // =======================================================
      // 1. FIND LATEST IN-PROGRESS SESSION
      // =======================================================

      let sessionQuery = supabase
        .from("practice_sessions")
        .select(
          `
          id,
          practice_mode,
          domain_id,
          topic_id,
          difficulty_mode,
          question_count,
          correct_count,
          incorrect_count,
          accuracy_pct,
          session_status,
          started_at
          `
        )
        .eq(
          "user_id",
          session.user.id
        )
        .eq(
          "session_status",
          "in_progress"
        );

      // =======================================================
      // EXACT SESSION RESTORE
      //
      // If App.js provides a specific session ID,
      // restore that exact session.
      //
      // Otherwise keep the existing fallback behavior:
      // find the latest matching in-progress session.
      // =======================================================

      if (practiceSessionId) {

        sessionQuery =
          sessionQuery.eq(
            "id",
            practiceSessionId
          );

      } else {

        if (practiceMode) {
          sessionQuery =
            sessionQuery.eq(
              "practice_mode",
              practiceMode
            );
        }

        if (domainId) {
          sessionQuery =
            sessionQuery.eq(
              "domain_id",
              domainId
            );
        }

        if (topicId) {
          sessionQuery =
            sessionQuery.eq(
              "topic_id",
              topicId
            );
        }

      }

      const {
        data: sessionData,
        error: sessionError,
      } = await sessionQuery
        .order(
          "started_at",
          {
            ascending: false,
          }
        )
        .limit(1)
        .maybeSingle();

      if (sessionError) {
        console.error(
          "RESUME PRACTICE SESSION ERROR:",
          sessionError
        );

        setLoadingPracticeQuestions(
          false
        );

        return null;
      }

      if (!sessionData) {
        setLoadingPracticeQuestions(
          false
        );

        return null;
      }


      // =======================================================
      // 2. FETCH FROZEN QUESTION ORDER
      // =======================================================

      const {
        data: frozenRows,
        error: frozenError,
      } = await supabase
        .from(
          "practice_session_questions"
        )
        .select(
          `
          question_id,
          display_order
          `
        )
        .eq(
          "practice_session_id",
          sessionData.id
        )
        .order(
          "display_order",
          {
            ascending: true,
          }
        );

      if (frozenError) {
        console.error(
          "RESUME FROZEN QUESTIONS ERROR:",
          frozenError
        );

        setLoadingPracticeQuestions(
          false
        );

        return null;
      }

      if (!frozenRows?.length) {
        setLoadingPracticeQuestions(
          false
        );

        return null;
      }

      // =======================================================
      // 3. QUESTION IDS
      // =======================================================

      const questionIds =
        frozenRows.map(
          (row) =>
            row.question_id
        );


      // =======================================================
      // 4. FETCH QUESTION CONTENT
      // =======================================================

      const {
        data: questionRows,
        error: questionError,
      } = await supabase
        .from("questions")
        .select(
          `
          id,
          question_text,
          difficulty_level,
          domain_id,
          topic_id
          `
        )
        .in(
          "id",
          questionIds
        );

      if (questionError) {
        console.error(
          "RESUME PRACTICE QUESTIONS ERROR:",
          questionError
        );

        setLoadingPracticeQuestions(
          false
        );

        return null;
      }


      // =======================================================
      // 5. FETCH OPTIONS
      // =======================================================

      const {
        data: optionRows,
        error: optionError,
      } = await supabase
        .from("question_options")
        .select(
          `
          id,
          question_id,
          option_key,
          option_text,
          display_order
          `
        )
        .in(
          "question_id",
          questionIds
        )
        .order(
          "display_order",
          {
            ascending: true,
          }
        );

      if (optionError) {
        console.error(
          "RESUME PRACTICE OPTIONS ERROR:",
          optionError
        );

        setLoadingPracticeQuestions(
          false
        );

        return null;
      }

      // =======================================================
      // 5B. FETCH QUESTION MEDIA
      // =======================================================

      const mediaMap =
        await fetchQuestionMedia(
          questionIds
        );

      // =======================================================
      // 6. RESTORE FROZEN ORDER
      // =======================================================

      const questionMap =
        new Map(
          (questionRows || []).map(
            (question) => [
              question.id,
              question,
            ]
          )
        );

      const orderedQuestionRows =
        questionIds
          .map(
            (id) =>
              questionMap.get(id)
          )
          .filter(Boolean);


      // =======================================================
      // 7. NORMALIZE QUESTIONS
      // =======================================================

      const normalizedQuestions =
        orderedQuestionRows.map(
          (question) => {
            const options =
              (optionRows || [])
                .filter(
                  (option) =>
                    option.question_id ===
                    question.id
                )
                .sort(
                  (a, b) =>
                    a.display_order -
                    b.display_order
                );

            return {
              id:
                question.id,

              question:
                question.question_text,

              difficulty:
                question.difficulty_level,

              domain_id:
                question.domain_id,

              topic_id:
                question.topic_id,

              options:
                options.map(
                  (option) =>
                    option.option_text
                ),

              optionRecords:
                options,

              media:
                mediaMap.get(
                  question.id
                ) || [],
            };
          }
        );


      // =======================================================
      // 8. FETCH EXISTING ANSWERS SECURELY
      //
      // Returns correct_option_id only for questions
      // this user has already answered.
      // =======================================================

      const {
        data: answerRows,
        error: answerError,
      } = await supabase.rpc(
        "get_practice_resume_answers",
        {
          p_practice_session_id:
            sessionData.id,
        }
      );

      if (answerError) {
        console.error(
          "RESUME PRACTICE ANSWERS ERROR:",
          answerError
        );

        setLoadingPracticeQuestions(
          false
        );

        return null;
      }


      // =======================================================
      // 9. FIND NEXT QUESTION INDEX
      //
      // First question without an answer.
      // If all are answered, stays on the last question.
      // =======================================================

      const answeredQuestionIds =
        new Set(
          (answerRows || []).map(
            (answer) =>
              answer.question_id
          )
        );

      let nextQuestionIndex =
        normalizedQuestions.findIndex(
          (question) =>
            !answeredQuestionIds.has(
              question.id
            )
        );

      if (nextQuestionIndex === -1) {
        nextQuestionIndex =
          Math.max(
            normalizedQuestions.length -
              1,
            0
          );
      }


      // =======================================================
      // 10. RESTORE STATE INSIDE HOOK
      // =======================================================

      setPracticeQuestions(
        normalizedQuestions
      );

      setCurrentPracticeSessionId(
        sessionData.id
      );

      // -------------------------------------------------------
      // Remember the exact restored session.
      //
      // This also upgrades older sessions that were created
      // before exact-session localStorage support existed.
      // -------------------------------------------------------

      localStorage.setItem(
        "active_practice_session_id",
        sessionData.id
      );


      setLoadingPracticeQuestions(
        false
      );


      // =======================================================
      // 11. RETURN EVERYTHING APP.JS NEEDS
      // =======================================================

      return {
        session:
          sessionData,

        questions:
          normalizedQuestions,

        answers:
          answerRows || [],

        nextQuestionIndex,
      };
    } catch (error) {
      console.error(
        "RESUME PRACTICE SESSION UNEXPECTED ERROR:",
        error
      );

      setLoadingPracticeQuestions(
        false
      );

      return null;
    }
  };

  // =========================================================
  // GET ACTIVE SMART PRACTICE SUMMARY
  // =========================================================

  const getActivePracticeSummary = async () => {
    if (!session?.user?.id) {
      return null;
    }

    try {
      // -------------------------------------------------------
      // 1. Find latest unfinished Smart Practice
      // -------------------------------------------------------

      const {
        data: sessionData,
        error: sessionError,
      } = await supabase
        .from("practice_sessions")
        .select(
          `
          id,
          practice_mode,
          session_status,
          started_at
          `
        )
        .eq(
          "user_id",
          session.user.id
        )
        .eq(
          "practice_mode",
          "adaptive"
        )
        .eq(
          "session_status",
          "in_progress"
        )
        .order(
          "started_at",
          {
            ascending: false,
          }
        )
        .limit(1)
        .maybeSingle();

      if (sessionError) {
        console.error(
          "GET ACTIVE PRACTICE SUMMARY ERROR:",
          sessionError
        );

        return null;
      }

      if (!sessionData) {
        return null;
      }

      // -------------------------------------------------------
      // 2. Count frozen questions
      // -------------------------------------------------------

      const {
        count: totalQuestions,
        error: questionCountError,
      } = await supabase
        .from(
          "practice_session_questions"
        )
        .select(
          "id",
          {
            count: "exact",
            head: true,
          }
        )
        .eq(
          "practice_session_id",
          sessionData.id
        );

      if (questionCountError) {
        console.error(
          "GET ACTIVE PRACTICE QUESTION COUNT ERROR:",
          questionCountError
        );

        return null;
      }

      // -------------------------------------------------------
      // 3. Count completed questions
      // -------------------------------------------------------

      const {
        count: completedQuestions,
        error: answerCountError,
      } = await supabase
        .from("practice_answers")
        .select(
          "id",
          {
            count: "exact",
            head: true,
          }
        )
        .eq(
          "practice_session_id",
          sessionData.id
        );

      if (answerCountError) {
        console.error(
          "GET ACTIVE PRACTICE ANSWER COUNT ERROR:",
          answerCountError
        );

        return null;
      }

      return {
        sessionId:
          sessionData.id,

        practiceMode:
          sessionData.practice_mode,

        startedAt:
          sessionData.started_at,

        completedQuestions:
          completedQuestions || 0,

        totalQuestions:
          totalQuestions || 0,
      };
    } catch (error) {
      console.error(
        "GET ACTIVE PRACTICE SUMMARY UNEXPECTED ERROR:",
        error
      );

      return null;
    }
  };

  // =========================================================
  // GET ACTIVE SUBJECT PRACTICE SUMMARIES
  //
  // Returns unfinished subject-practice rounds for
  // the current user, optionally filtered by domain.
  // =========================================================

  const getActiveSubjectPracticeSummaries =
    async ({
      domainId = null,
    } = {}) => {
      if (!session?.user?.id) {
        return [];
      }

      try {
        let sessionQuery = supabase
          .from("practice_sessions")
          .select(
            `
            id,
            practice_mode,
            domain_id,
            topic_id,
            session_status,
            started_at
            `
          )
          .eq(
            "user_id",
            session.user.id
          )
          .eq(
            "practice_mode",
            "subject"
          )
          .eq(
            "session_status",
            "in_progress"
          )
          .order(
            "started_at",
            {
              ascending: false,
            }
          );

        if (domainId) {
          sessionQuery =
            sessionQuery.eq(
              "domain_id",
              domainId
            );
        }

        const {
          data: sessionRows,
          error: sessionError,
        } = await sessionQuery;

        if (sessionError) {
          console.error(
            "GET ACTIVE SUBJECT PRACTICE SESSIONS ERROR:",
            sessionError
          );

          return [];
        }

        if (!sessionRows?.length) {
          return [];
        }

        const summaries =
          await Promise.all(
            sessionRows.map(
              async (sessionRow) => {
                const {
                  count:
                    totalQuestions,
                  error:
                    questionCountError,
                } = await supabase
                  .from(
                    "practice_session_questions"
                  )
                  .select(
                    "id",
                    {
                      count: "exact",
                      head: true,
                    }
                  )
                  .eq(
                    "practice_session_id",
                    sessionRow.id
                  );

                if (
                  questionCountError
                ) {
                  console.error(
                    "GET SUBJECT QUESTION COUNT ERROR:",
                    questionCountError
                  );

                  return null;
                }

                const {
                  count:
                    completedQuestions,
                  error:
                    answerCountError,
                } = await supabase
                  .from(
                    "practice_answers"
                  )
                  .select(
                    "id",
                    {
                      count: "exact",
                      head: true,
                    }
                  )
                  .eq(
                    "practice_session_id",
                    sessionRow.id
                  );

                if (
                  answerCountError
                ) {
                  console.error(
                    "GET SUBJECT ANSWER COUNT ERROR:",
                    answerCountError
                  );

                  return null;
                }

                return {
                  sessionId:
                    sessionRow.id,

                  practiceMode:
                    sessionRow.practice_mode,

                  domainId:
                    sessionRow.domain_id,

                  topicId:
                    sessionRow.topic_id,

                  startedAt:
                    sessionRow.started_at,

                  completedQuestions:
                    completedQuestions ||
                    0,

                  totalQuestions:
                    totalQuestions ||
                    0,
                };
              }
            )
          );

        return summaries.filter(
          Boolean
        );
      } catch (error) {
        console.error(
          "GET ACTIVE SUBJECT PRACTICE SUMMARIES UNEXPECTED ERROR:",
          error
        );

        return [];
      }
    };

  // =========================================================
  // ABANDON PRACTICE SESSION
  //
  // Used when the student intentionally starts
  // a brand-new round before finishing the current one.
  // =========================================================

  const abandonPracticeSession = async (
    practiceSessionId
  ) => {
    if (
      !practiceSessionId ||
      !session?.user?.id
    ) {
      return false;
    }

    try {
      const {
        error,
      } = await supabase
        .from("practice_sessions")
        .update({
          session_status:
            "abandoned",

          ended_at:
            new Date().toISOString(),
        })
        .eq(
          "id",
          practiceSessionId
        )
        .eq(
          "user_id",
          session.user.id
        )
        .eq(
          "session_status",
          "in_progress"
        );

      if (error) {
        console.error(
          "ABANDON PRACTICE SESSION ERROR:",
          error
        );

        // -------------------------------------------------------
        // Clear saved exact-session pointer only if it points
        // to the session we just abandoned.
        // -------------------------------------------------------

        const savedPracticeSessionId =
          localStorage.getItem(
            "active_practice_session_id"
          );

        if (
          savedPracticeSessionId ===
          practiceSessionId
        ) {
          localStorage.removeItem(
            "active_practice_session_id"
          );
        }

        return true;
      }

      return true;
    } catch (error) {
      console.error(
        "ABANDON PRACTICE SESSION UNEXPECTED ERROR:",
        error
      );

      return false;
    }
  };

  // =========================================================
  // COMPLETE PRACTICE SESSION
  // =========================================================

  const completePracticeSession =
    async (
      practiceSessionId
    ) => {
      if (!practiceSessionId) {
        return null;
      }

      const {
        data,
        error,
      } = await supabase
        .from(
          "practice_sessions"
        )
        .update({
          session_status:
            "completed",

          ended_at:
            new Date().toISOString(),
        })
        .eq(
          "id",
          practiceSessionId
        )
        .eq(
          "user_id",
          session?.user?.id
        )
        .select(
          `
          id,
          practice_mode,
          domain_id,
          topic_id,
          question_count,
          correct_count,
          incorrect_count,
          accuracy_pct,
          session_status,
          started_at,
          ended_at
          `
        )
        .single();

      if (error) {
        console.error(
          "COMPLETE PRACTICE SESSION ERROR:",
          error
        );

        return null;
      }

      // -------------------------------------------------------
      // The completed session should no longer be restored
      // after refresh.
      // -------------------------------------------------------

      const savedPracticeSessionId =
        localStorage.getItem(
          "active_practice_session_id"
        );

      if (
        savedPracticeSessionId ===
        practiceSessionId
      ) {
        localStorage.removeItem(
          "active_practice_session_id"
        );
      }

      return data;
    };


  // =========================================================
  // SUBMIT PRACTICE ANSWER
  //
  // Browser does NOT grade the answer.
  //
  // Supabase RPC securely reads question_answer_keys.
  // =========================================================

  const submitPracticeAnswer =
    async ({
      practiceSessionId,
      questionId,
      selectedOptionId,
    }) => {
      if (!session?.user?.id) {
        return {
          success: false,

          error:
            "Please log in first.",
        };
      }

      if (
        !practiceSessionId ||
        !questionId ||
        !selectedOptionId
      ) {
        return {
          success: false,

          error:
            "Missing practice session, question, or selected option.",
        };
      }

      setSubmittingPracticeAnswer(
        true
      );

      try {
        const {
          data,
          error,
        } = await supabase.rpc(
          "submit_practice_answer",
          {
            p_practice_session_id:
              practiceSessionId,

            p_question_id:
              questionId,

            p_selected_option_id:
              selectedOptionId,
          }
        );

        if (error) {
          console.error(
            "SUBMIT PRACTICE ANSWER ERROR:",
            error
          );

          return {
            success: false,

            error:
              error.message,
          };
        }

        const result =
          data?.[0];

        if (!result) {
          return {
            success: false,

            error:
              "No result returned from practice answer submission.",
          };
        }

        return {
          success: true,

          practiceAnswerId:
            result.practice_answer_id,

          isCorrect:
            result.is_correct,

          correctOptionId:
            result.correct_option_id,

          // Already returned by the upgraded RPC.
          // Useful later for Adaptive UI / Wrong Question.
          masteryLevel:
            result.mastery_level,

          nextReviewAt:
            result.next_review_at,
        };
      } catch (error) {
        console.error(
          "SUBMIT PRACTICE ANSWER UNEXPECTED ERROR:",
          error
        );

        return {
          success: false,

          error:
            error.message ||
            "Unable to submit practice answer.",
        };
      } finally {
        setSubmittingPracticeAnswer(
          false
        );
      }
    };


  // =========================================================
  // RETURN
  // =========================================================

  return {
    practiceQuestions,
    loadingPracticeQuestions,
    fetchPracticeQuestions,
    fetchWrongQuestions,
    currentPracticeSessionId,
    startingPracticeSession,
    startPracticeSession,
    submittingPracticeAnswer,
    submitPracticeAnswer,
    completePracticeSession,
    resumePracticeSession,
    getActivePracticeSummary,
    getActiveSubjectPracticeSummaries,
    abandonPracticeSession,
  };
}